import React, { useEffect, useState, useRef } from 'react';
import { Volume2, VolumeX, Play, Pause, SkipForward, Menu, X } from 'lucide-react';
import { HEALING_FILE_NAMES, GCS_BASE_URL } from '../data/videos';
import { supabase } from '../lib/supabase';

export const HealingPlayer: React.FC = () => {
  const [videoUrl, setVideoUrl] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showMenu, setShowMenu] = useState(false);

  const pickRandomVideo = async () => {
    setVideoLoading(true);
    try {
      if (HEALING_FILE_NAMES.length === 0) return;

      const randomIndex = Math.floor(Math.random() * HEALING_FILE_NAMES.length);
      const fileName = HEALING_FILE_NAMES[randomIndex];

      // Construction of GCS public URL
      const publicUrl = `${GCS_BASE_URL}/${fileName}`;

      setVideoUrl(publicUrl);
      setError(null);
    } catch (e) {
      console.error("Failed to set video URL:", e);
      setError("Failed to load video.");
    } finally {
      setVideoLoading(false);
    }
  };

  useEffect(() => {
    if (!videoUrl) {
      pickRandomVideo();
    }
  }, []);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(console.error);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const newMuted = !videoRef.current.muted;
    videoRef.current.muted = newMuted;
    setIsMuted(newMuted);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center py-12 px-4 font-sans space-y-10 relative">

      {/* Top Left: Main Menu Button */}
      <button
        onClick={() => setShowMenu(true)}
        className="fixed top-4 left-4 z-50 p-3 text-blue-400 hover:text-blue-300 transition-colors rounded-full hover:bg-blue-900/20 backdrop-blur-sm"
        aria-label="Menu"
      >
        <Menu size={28} />
      </button>

      {/* Menu Overlay */}
      {showMenu && (
        <div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex flex-col items-center justify-center space-y-8 p-8 transition-all duration-300 animate-in fade-in">
          <button
            onClick={() => setShowMenu(false)}
            className="absolute top-4 left-4 p-3 text-blue-400 hover:text-blue-300 rounded-full hover:bg-blue-900/20"
            aria-label="Close Menu"
          >
            <X size={28} />
          </button>

          <h2 className="text-3xl text-blue-300 font-light tracking-[0.2em] mb-8 drop-shadow-[0_0_10px_rgba(59,130,246,0.5)]">MENU</h2>

          <div className="w-16 h-px bg-blue-900/50 my-4" />

          <a
            href="https://teshiokayumi.github.io/main/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg text-white hover:text-blue-400 transition-colors"
          >
            更新情報
          </a>

          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSeWZ_xdeX-0tYcFsofqpu3_mtBUHj8eDTPW4xQL5RcTe93exQ/viewform?usp=publish-editor"
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg text-white hover:text-blue-400 transition-colors"
          >
            お問合せ
          </a>

          <a
            href="https://sugerpowder.my.canva.site/page-2"
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg text-white hover:text-blue-400 transition-colors"
          >
            特定商取引法に基づく表記
          </a>

          <div className="text-blue-900/50 text-sm mt-8">
            (c)kayumi teshio2025
          </div>
        </div>
      )}

      {/* Unmute Overlay Button */}
      {isMuted && videoUrl && (
        <button
          onClick={toggleMute}
          className="fixed top-6 left-1/2 transform -translate-x-1/2 z-40 bg-blue-600/90 hover:bg-blue-500 text-white px-6 py-3 rounded-full shadow-[0_0_20px_rgba(59,130,246,0.5)] animate-bounce flex items-center gap-2 transition-all duration-300 backdrop-blur-sm border border-blue-400/30"
        >
          <Volume2 size={20} />
          <span className="font-medium tracking-wide">タップして音声をオンにする</span>
        </button>
      )}

      {/* Top: Title */}
      <div className="w-full text-center space-y-4">
        <h1 className="text-3xl md:text-5xl font-light text-blue-400 tracking-[0.2em] drop-shadow-[0_0_10px_rgba(59,130,246,0.5)] animate-pulse">
          癒しをあなたに
        </h1>
        <p className="text-blue-300 text-sm md:text-lg font-light tracking-widest opacity-80">
          cv 穴田アナ
        </p>
      </div>

      {/* Middle: Video */}
      <div className="w-full flex justify-center">
        <div className="w-full max-w-[560px] aspect-video border border-blue-900/30 shadow-[0_0_30px_rgba(0,0,255,0.1)] bg-black overflow-hidden bg-center bg-cover">
          {videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              autoPlay
              muted={isMuted}
              loop
              playsInline
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-blue-900">
              {videoLoading ? "Loading content..." : (error || "No Content")}
            </div>
          )}
        </div>
      </div>

      {/* Bottom: Buttons */}
      <div className="w-full flex flex-col items-center space-y-8">
        <div className="flex items-center gap-8">
          <button
            onClick={togglePlay}
            className="group p-5 rounded-full border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400 hover:text-blue-300 transition-all duration-500 backdrop-blur-md shadow-[0_0_20px_rgba(0,0,0,0.5)]"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={32} strokeWidth={1.5} /> : <Play size={32} strokeWidth={1.5} className="ml-1" />}
          </button>

          <button
            onClick={toggleMute}
            className="group p-5 rounded-full border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400 hover:text-blue-300 transition-all duration-500 backdrop-blur-md shadow-[0_0_20px_rgba(0,0,0,0.5)]"
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <VolumeX size={32} strokeWidth={1.5} /> : <Volume2 size={32} strokeWidth={1.5} />}
          </button>

          <button
            onClick={() => pickRandomVideo()}
            className="group p-5 rounded-full border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400 hover:text-blue-300 transition-all duration-500 backdrop-blur-md shadow-[0_0_20px_rgba(0,0,0,0.5)]"
            aria-label="Next Video"
          >
            <SkipForward size={32} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
};
